export const MAX_FILES_PER_UPLOAD = 10
export const UPLOAD_CONCURRENCY = 3

export interface SelectableFile {
  name: string
  size: number
  lastModified: number
}

export const fileSelectionKey = (file: SelectableFile) => `${file.name}\0${file.size}\0${file.lastModified}`

/**
 * Adds incoming files to the current selection.
 * The same file picked again is skipped. A multiple selection stops at maxFiles.
 * A single-file control replaces the previous selection.
 */
export function mergeFileSelection<T extends SelectableFile>({
  existing,
  incoming,
  multiple,
  maxFiles,
  validate,
}: {
  existing: T[]
  incoming: T[]
  multiple: boolean
  maxFiles?: number
  validate: (file: T) => { valid: boolean; error?: string }
}): { files: T[]; rejections: string[] } {
  const files = multiple ? [...existing] : []
  const seen = new Set(files.map(fileSelectionKey))
  const rejections: string[] = []

  for (const file of incoming) {
    const key = fileSelectionKey(file)
    if (seen.has(key)) {
      rejections.push(`"${file.name}" is already selected.`)
      continue
    }
    const validation = validate(file)
    if (!validation.valid) {
      rejections.push(`"${file.name}" ${validation.error ?? 'could not be added'}.`)
      continue
    }
    if (multiple && maxFiles && files.length >= maxFiles) {
      rejections.push(`"${file.name}" was not added. You can upload up to ${maxFiles} files at a time.`)
      continue
    }
    seen.add(key)
    files.push(file)
  }

  return { files, rejections }
}

/** Runs work for each item, with at most `limit` items in flight. One failure does not stop the rest. */
export async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>
): Promise<void> {
  const queue = [...items]
  const runners = Array.from({ length: Math.min(Math.max(limit, 1), queue.length) }, async () => {
    while (queue.length > 0) {
      const item = queue.shift()
      if (item === undefined) return
      try {
        await worker(item)
      } catch {
        // The worker records the failure. The remaining items still run.
      }
    }
  })
  await Promise.all(runners)
}
